import {
  Box,
  BoxProps,
  Fade,
  IconButton,
  Typography,
  ClickAwayListener,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { forwardRef, useState, useCallback, useRef, useEffect } from "react";
import { FONT_SIZES, FONT_WEIGHTS } from "../../constants/styles/typography";
import { ChevronLeft, ChevronRight, CalendarToday } from "@mui/icons-material";

// DatePicker Props 인터페이스
export interface DatePickerProps
  extends Omit<BoxProps, "onChange" | "onToggle"> {
  /**
   * 선택된 시작 날짜
   */
  startDate?: Date | null;
  /**
   * 선택된 종료 날짜
   */
  endDate?: Date | null;
  /**
   * 날짜 범위가 변경될 때 호출되는 콜백 함수
   */
  onChange?: (startDate: Date | null, endDate: Date | null) => void;
  /**
   * 달력이 열려있는지 여부
   */
  open?: boolean;
  /**
   * 달력 열기/닫기 콜백 함수
   */
  onToggle?: (open: boolean) => void;
  /**
   * 컴포넌트가 비활성화되었는지 여부
   */
  disabled?: boolean;
  /**
   * 플레이스홀더 텍스트
   */
  placeholder?: string;
  /**
   * 라벨 텍스트
   */
  label?: string;
  /**
   * 도움말 텍스트
   */
  helperText?: string;
  /**
   * 에러 상태 여부
   */
  error?: boolean;
  /**
   * 최소 선택 가능한 날짜
   */
  minDate?: Date;
  /**
   * 최대 선택 가능한 날짜
   */
  maxDate?: Date;
  /**
   * 달력 컨테이너의 정렬 위치
   * - left/center/right: 수평 정렬, 자동으로 위/아래 조정
   * - upper: 강제로 인풋창 위로
   * - down: 강제로 인풋창 아래로
   */
  alignment?: "left" | "center" | "right" | "upper" | "down";
  /**
   * 단일 날짜 선택만 가능하도록 제한
   * true일 경우 범위 선택이 불가능하고 항상 단일 날짜만 선택 가능
   */
  singleDateOnly?: boolean;
  /**
   * 테스트 앵커. 루트 컨테이너로 전파된다(`...props`). 커스텀 캘린더라 native input 노드가 없으므로
   * `getByTestId().fill()` 대상이 아니라 **클릭(캘린더 열기)** 앵커로 쓴다. 값 규약은 `src/test/testIds.ts`.
   */
  "data-testid"?: string;
}

/**
 * 달력 위치를 정하는 입력 전부 — 바로 아래 `getCalendarContainerStyles` 가 읽는 값과 1:1 이다.
 * 창 높이가 들어 있는 이유는 그 함수가 아래쪽 여유(`window.innerHeight - bottom`)로 위/아래 열림을
 * 정하기 때문이다.
 *
 * ⚠️ `getCalendarContainerStyles` 가 읽는 값이 늘면 이 타입과 `calendarPlacementOf` 도 함께 늘려야
 * 한다. 갈리면 입력이 움직였는데도 위치가 따라오지 않는다 — 그래서 세 선언을 붙여 둔다.
 */
export interface CalendarPlacement {
  width: number;
  top: number;
  bottom: number;
  viewportHeight: number;
}

export const calendarPlacementOf = (
  rect: DOMRect,
  viewportHeight: number
): CalendarPlacement => ({
  width: rect.width,
  top: rect.top,
  bottom: rect.bottom,
  viewportHeight,
});

/**
 * 두 입력이 같은 달력 위치를 만드는지 판정한다 — `getBoundingClientRect()` 는 호출마다 새 객체를
 * 돌려주므로 참조 비교로는 항상 "변했다"가 된다.
 */
export const isSameCalendarPlacement = (
  placement1?: CalendarPlacement,
  placement2?: CalendarPlacement
): boolean =>
  placement1 === placement2 ||
  (placement1 !== undefined &&
    placement2 !== undefined &&
    placement1.width === placement2.width &&
    placement1.top === placement2.top &&
    placement1.bottom === placement2.bottom &&
    placement1.viewportHeight === placement2.viewportHeight);

/**
 * 입력창 가장자리와 달력 사이 간격 — 측정 전 폴백(`marginTop`)과 측정 뒤 위/아래 열림
 * (`OPEN_ABOVE`·`OPEN_BELOW`)이 함께 읽는다. 갈리면 정렬 모드·열림 방향마다, 또는 측정 전과
 * 측정 뒤 사이에 간격이 달라진다. 격자 칸 간격(`CALENDAR_COLUMN_TRACK.gap`)과는 값만 같고 의미가 다르다.
 */
const CALENDAR_INPUT_GAP = "4px";
const OPEN_ABOVE = { bottom: `calc(100% + ${CALENDAR_INPUT_GAP})` } as const;
const OPEN_BELOW = { top: `calc(100% + ${CALENDAR_INPUT_GAP})` } as const;

// 달력 컨테이너 스타일 (alignment에 따라 동적 조정)
const getCalendarContainerStyles = (
  alignment: "left" | "center" | "right" | "upper" | "down",
  inputRect?: DOMRect
) => {
  const baseStyles = {
    position: "absolute" as const,
    zIndex: 9999,
    backgroundColor: "#ffffff",
    borderRadius: "8px",
    boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)",
    border: "1px solid #e0e0e0",
    padding: "16px",
  };

  if (!inputRect) {
    // fallback for when inputRect is not available
    return {
      ...baseStyles,
      minWidth: "300px",
      top: "100%",
      left: "50%",
      transform: "translateX(-50%)",
      marginTop: CALENDAR_INPUT_GAP,
    };
  }

  // 달력 높이 (대략 350px)
  const calendarHeight = 350;
  const calendarWidth = inputRect.width;

  // upper/down 강제 옵션 처리
  if (alignment === "upper") {
    return {
      ...baseStyles,
      width: `${calendarWidth}px`,
      ...OPEN_ABOVE,
      left: "0",
    };
  }

  if (alignment === "down") {
    return {
      ...baseStyles,
      width: `${calendarWidth}px`,
      ...OPEN_BELOW,
      left: "0",
    };
  }

  // left/center/right는 자동 위/아래 조정
  const spaceBelow = window.innerHeight - inputRect.bottom;
  const spaceAbove = inputRect.top;

  // 아래쪽 공간이 부족하면 위로 열기
  const shouldOpenUp =
    spaceBelow < calendarHeight && spaceAbove > calendarHeight;

  switch (alignment) {
    case "left":
      return {
        ...baseStyles,
        width: `${calendarWidth}px`,
        ...(shouldOpenUp ? OPEN_ABOVE : OPEN_BELOW),
        left: "0",
      };
    case "right":
      return {
        ...baseStyles,
        width: `${calendarWidth}px`,
        ...(shouldOpenUp ? OPEN_ABOVE : OPEN_BELOW),
        right: "0",
      };
    case "center":
    default:
      return {
        ...baseStyles,
        width: `${calendarWidth}px`,
        ...(shouldOpenUp ? OPEN_ABOVE : OPEN_BELOW),
        left: "50%",
        transform: "translateX(-50%)",
      };
  }
};

// 헤더 스타일
const HeaderContainer = styled(Box)(() => ({
  marginBottom: "16px",
}));

const HeaderTitle = styled(Typography)(({ theme }) => ({
  fontSize: FONT_SIZES.xs,
  fontWeight: FONT_WEIGHTS.regular,
  color: theme.palette.text.secondary,
  letterSpacing: "1px",
  marginBottom: "8px",
}));

const SelectedDateRange = styled(Typography)(({ theme }) => ({
  fontSize: "34px",
  fontWeight: FONT_WEIGHTS.regular,
  color: theme.palette.text.primary,
  letterSpacing: "0.25px",
  lineHeight: "42px",
}));

// 네비게이션 스타일
const NavigationContainer = styled(Box)(() => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  marginBottom: "16px",
}));

const MonthYearText = styled(Typography)(({ theme }) => ({
  fontSize: FONT_SIZES.md,
  fontWeight: FONT_WEIGHTS.medium,
  color: theme.palette.text.primary,
  letterSpacing: "0.15px",
}));

const NavButton = styled(IconButton)(({ theme }) => ({
  width: "34px",
  height: "34px",
  borderRadius: "50%",
  "&:hover": {
    backgroundColor: theme.palette.action.hover,
  },
}));

/**
 * 요일 머리글 행(`DayHeaderContainer`)과 날짜 칸 격자(`CalendarGrid`)가 함께 쓰는 7열 트랙.
 * 두 격자는 위아래 형제로 서므로 열 정의·간격이 갈리면 머리글이 날짜 열에서 어긋난다.
 */
const CALENDAR_COLUMN_TRACK = {
  display: "grid",
  gridTemplateColumns: "repeat(7, 1fr)",
  gap: "4px",
} as const;

// 요일 헤더 스타일
const DayHeaderContainer = styled(Box)(() => ({
  ...CALENDAR_COLUMN_TRACK,
  marginBottom: "8px",
}));

const DayHeaderCell = styled(Box)(({ theme }) => ({
  width: "100%",
  aspectRatio: "1",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  fontSize: FONT_SIZES.sm,
  fontWeight: FONT_WEIGHTS.regular,
  color: theme.palette.text.secondary,
  letterSpacing: "0.17px",
}));

// 달력 그리드 스타일
const CalendarGrid = styled(Box)(() => ({
  ...CALENDAR_COLUMN_TRACK,
}));

const DayCell = styled(Box, {
  shouldForwardProp: (prop) =>
    ![
      "isSelected",
      "isInRange",
      "isStartDate",
      "isEndDate",
      "isDisabled",
      "isOtherMonth",
    ].includes(prop as string),
})<{
  isSelected?: boolean;
  isInRange?: boolean;
  isStartDate?: boolean;
  isEndDate?: boolean;
  isDisabled?: boolean;
  isOtherMonth?: boolean;
}>(
  ({
    theme,
    isSelected,
    isInRange,
    isStartDate,
    isEndDate,
    isDisabled,
    isOtherMonth,
  }) => ({
    width: "100%",
    aspectRatio: "1",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: FONT_SIZES.sm,
    fontWeight: FONT_WEIGHTS.regular,
    letterSpacing: "0.17px",
    cursor: isDisabled ? "not-allowed" : "pointer",
    borderRadius: "50%",
    transition: "all 0.2s ease-in-out",
    position: "relative",

    // 기본 상태
    color: isOtherMonth
      ? theme.palette.text.disabled
      : theme.palette.text.primary,
    opacity: isOtherMonth ? 0.38 : 0.87,

    // 선택된 날짜 (시작일/종료일)
    ...(isSelected && {
      backgroundColor: theme.palette.primary.main,
      color: theme.palette.primary.contrastText,
      border: `1px solid ${theme.palette.primary.main}`,
      opacity: 1,
    }),

    // 범위 내 날짜
    ...(isInRange &&
      !isSelected && {
        backgroundColor: "rgba(33, 150, 243, 0.12)",
        color: theme.palette.text.primary,
        opacity: 0.87,
      }),

    // 비활성화된 날짜
    ...(isDisabled && {
      color: theme.palette.text.disabled,
      cursor: "not-allowed",
      opacity: 0.38,
    }),

    // 호버 상태
    "&:hover": {
      ...(!isDisabled && {
        backgroundColor: theme.palette.action.hover,
        opacity: 1,
      }),
    },
  })
);

// TextField 스타일
const TextFieldContainer = styled(Box)(({ theme }) => ({
  position: "relative",
  width: "100%",
}));

const InputContainer = styled(Box, {
  shouldForwardProp: (prop) => prop !== "error" && prop !== "focused",
})<{ error?: boolean; focused?: boolean }>(({ theme, error, focused }) => ({
  display: "flex",
  alignItems: "center",
  border: `1px solid ${
    error
      ? theme.palette.error.main
      : focused
      ? theme.palette.primary.main
      : "rgba(0, 0, 0, 0.16)"
  }`,
  borderRadius: "4px",
  padding: "8px 12px",
  backgroundColor: theme.palette.common.white,
  cursor: "pointer",
  transition: "border-color 0.2s ease-in-out",
  "&:hover": {
    borderColor: error ? theme.palette.error.main : theme.palette.primary.main,
  },
}));

const InputContent = styled(Box)(() => ({
  display: "flex",
  alignItems: "center",
  flex: 1,
  gap: "8px",
}));

const InputText = styled(Typography)(({ theme }) => ({
  fontSize: FONT_SIZES.sm,
  fontWeight: FONT_WEIGHTS.regular,
  color: theme.palette.text.primary,
  letterSpacing: "0.15px",
  flex: 1,
}));

const PlaceholderText = styled(Typography)(({ theme }) => ({
  fontSize: FONT_SIZES.sm,
  fontWeight: FONT_WEIGHTS.regular,
  color: theme.palette.text.disabled,
  letterSpacing: "0.15px",
  flex: 1,
}));

const LabelText = styled(Typography)(({ theme }) => ({
  fontSize: FONT_SIZES.xs,
  fontWeight: FONT_WEIGHTS.regular,
  color: theme.palette.primary.main,
  letterSpacing: "0.15px",
  marginBottom: "4px",
}));

const HelperText = styled(Typography)(({ theme }) => ({
  fontSize: FONT_SIZES.xs,
  fontWeight: FONT_WEIGHTS.regular,
  color: theme.palette.text.secondary,
  letterSpacing: "0.4px",
  marginTop: "4px",
}));

// 유틸리티 함수들
const formatDate = (date: Date): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}/${month}/${day}`;
};

const formatDateRange = (
  startDate: Date | null,
  endDate: Date | null
): string => {
  // 단일 선택일 때 (startDate만 있는 경우)
  if (startDate && !endDate) {
    return formatDate(startDate);
  }
  // 범위 선택일 때 (startDate와 endDate가 모두 있는 경우)
  if (startDate && endDate) {
    // 시작일이 종료일보다 큰 경우 자동으로 순서 변경
    if (startDate > endDate) {
      return `${formatDate(endDate)} - ${formatDate(startDate)}`;
    }
    return `${formatDate(startDate)} - ${formatDate(endDate)}`;
  }
  return "";
};

const formatMonthYear = (date: Date): string => {
  const year = date.getFullYear();
  const month = date.getMonth() + 1;
  return `${year} ${month}월`;
};

const getDaysInMonth = (date: Date): number => {
  return new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();
};

const getFirstDayOfMonth = (date: Date): number => {
  return new Date(date.getFullYear(), date.getMonth(), 1).getDay();
};

const isSameDay = (date1: Date, date2: Date): boolean => {
  return (
    date1.getFullYear() === date2.getFullYear() &&
    date1.getMonth() === date2.getMonth() &&
    date1.getDate() === date2.getDate()
  );
};

const isDateInRange = (
  date: Date,
  startDate: Date | null,
  endDate: Date | null
): boolean => {
  if (!startDate || !endDate) return false;

  // 시작일과 종료일 자체는 범위에 포함하지 않음 (시각적 하이라이트용)
  if (isSameDay(date, startDate) || isSameDay(date, endDate)) {
    return false;
  }

  // 시작일과 종료일의 순서와 관계없이 범위 내인지 확인
  const minDate = startDate < endDate ? startDate : endDate;
  const maxDate = startDate > endDate ? startDate : endDate;
  return date > minDate && date < maxDate;
};

const isDateDisabled = (
  date: Date,
  minDate?: Date,
  maxDate?: Date
): boolean => {
  if (minDate && date < minDate) return true;
  if (maxDate && date > maxDate) return true;
  return false;
};

export const DatePicker = forwardRef<HTMLDivElement, DatePickerProps>(
  (
    {
      startDate = null,
      endDate = null,
      onChange,
      open = false,
      onToggle,
      disabled = false,
      placeholder = "YYYY/MM/DD - YYYY/MM/DD",
      label,
      helperText,
      error = false,
      minDate,
      maxDate,
      alignment = "center",
      singleDateOnly = false,
      sx,
      ...props
    },
    ref
  ) => {
    const [currentMonth, setCurrentMonth] = useState(new Date());
    const [isOpen, setIsOpen] = useState(open);
    const [inputRect, setInputRect] = useState<DOMRect | undefined>();
    const inputRef = useRef<HTMLDivElement>(null);

    // open prop과 state 동기화
    useEffect(() => {
      setIsOpen(open);
    }, [open]);

    useEffect(() => {
      if (singleDateOnly && startDate) {
        setCurrentMonth(
          new Date(startDate.getFullYear(), startDate.getMonth(), 1)
        );
      } else if (!startDate && !endDate) {
        setCurrentMonth(new Date());
      }
    }, []);

    // 직전에 반영한 위치 입력 — 같은 입력이면 상태를 다시 쓰지 않는다. 상태(`inputRect`)만으로는
    // 판정할 수 없다: 창 높이는 상태에 없고 rect 는 매번 새 객체다.
    const lastPlacementRef = useRef<CalendarPlacement | undefined>(undefined);

    const syncInputRect = useCallback(() => {
      if (!inputRef.current) return;
      const next = inputRef.current.getBoundingClientRect();
      const placement = calendarPlacementOf(next, window.innerHeight);
      if (isSameCalendarPlacement(lastPlacementRef.current, placement)) return;
      lastPlacementRef.current = placement;
      setInputRect(next);
    }, []);

    const handleToggle = useCallback(() => {
      if (disabled) return;
      const newOpen = !isOpen;
      setIsOpen(newOpen);
      onToggle?.(newOpen);

      if (newOpen) {
        syncInputRect();
      }
    }, [disabled, isOpen, onToggle, syncInputRect]);

    useEffect(() => {
      if (!isOpen) return;
      // 열림 직후 첫 측정은 합치지 않는다 — 한 프레임을 미루면 폴백 위치로 깜빡인다.
      syncInputRect();

      // 뷰포트 변화는 프레임당 1회만 측정한다. 핸들러마다 읽으면 강제 레이아웃이 이벤트 수만큼 발생한다.
      let frame: number | null = null;
      const onViewportChange = () => {
        if (frame !== null) return;
        frame = requestAnimationFrame(() => {
          frame = null;
          syncInputRect();
        });
      };

      window.addEventListener("resize", onViewportChange);
      window.addEventListener("scroll", onViewportChange);

      return () => {
        if (frame !== null) cancelAnimationFrame(frame);
        window.removeEventListener("resize", onViewportChange);
        window.removeEventListener("scroll", onViewportChange);
      };
    }, [isOpen, syncInputRect]);

    const handleDateClick = useCallback(
      (date: Date) => {
        if (disabled || isDateDisabled(date, minDate, maxDate)) return;

        // 클릭한 날짜가 현재 표시된 달에 있는지 확인
        const clickedDateMonth = date.getFullYear() * 12 + date.getMonth();
        const currentMonthValue =
          currentMonth.getFullYear() * 12 + currentMonth.getMonth();

        // 두 개가 다 선택된 상태에서 다른 달로 이동한 후 날짜를 클릭한 경우, 기존 선택 초기화
        if (startDate && endDate) {
          const startDateMonth =
            startDate.getFullYear() * 12 + startDate.getMonth();
          const endDateMonth = endDate.getFullYear() * 12 + endDate.getMonth();

          // 클릭한 날짜가 현재 표시된 달에 있고, 선택된 날짜 범위가 다른 달에 있으면 초기화
          if (
            clickedDateMonth === currentMonthValue &&
            (startDateMonth !== currentMonthValue ||
              endDateMonth !== currentMonthValue)
          ) {
            onChange?.(date, null);
            return;
          }
        }

        // 단일 선택만 가능한 경우
        if (singleDateOnly) {
          // 같은 날짜를 다시 클릭하면 선택 해제
          if (startDate && isSameDay(date, startDate)) {
            onChange?.(null, null);
            return;
          }
          // 항상 단일 선택만
          onChange?.(date, null);
          return;
        }

        // 1. startDate와 endDate가 모두 있는 경우 (범위 선택 완료)
        // 두 개가 모두 선택된 상태에서 어떤 날짜를 클릭하든 무조건 초기화하고 새로운 선택 시작
        if (startDate && endDate) {
          onChange?.(date, null);
          return;
        }

        // 2. startDate만 있는 경우 (범위 선택 중) - 종료일 선택
        if (startDate && !endDate) {
          // 같은 날짜를 다시 클릭하면 단일 선택으로 리셋
          if (isSameDay(date, startDate)) {
            onChange?.(date, null);
            return;
          }

          // 범위 완성 - 항상 작은 날짜가 시작일, 큰 날짜가 종료일이 되도록
          const rangeMinDate = date < startDate ? date : startDate;
          const rangeMaxDate = date > startDate ? date : startDate;
          onChange?.(rangeMinDate, rangeMaxDate);
          return;
        }

        // 3. startDate와 endDate가 모두 없는 경우 - 새로운 범위 시작
        onChange?.(date, null);
      },
      [
        startDate,
        endDate,
        onChange,
        disabled,
        minDate,
        maxDate,
        singleDateOnly,
        currentMonth,
      ]
    );

    const handlePrevMonth = useCallback(() => {
      setCurrentMonth(
        (prev) => new Date(prev.getFullYear(), prev.getMonth() - 1, 1)
      );
    }, []);

    const handleNextMonth = useCallback(() => {
      setCurrentMonth(
        (prev) => new Date(prev.getFullYear(), prev.getMonth() + 1, 1)
      );
    }, []);

    const renderCalendar = () => {
      const daysInMonth = getDaysInMonth(currentMonth);
      const firstDay = getFirstDayOfMonth(currentMonth);
      const days = [];

      // 이전 달의 날짜들 (빈 셀)
      for (let i = 0; i < firstDay; i++) {
        const prevMonth = new Date(
          currentMonth.getFullYear(),
          currentMonth.getMonth() - 1,
          0
        );
        const day = prevMonth.getDate() - firstDay + i + 1;
        const date = new Date(
          prevMonth.getFullYear(),
          prevMonth.getMonth(),
          day
        );

        days.push(
          <DayCell
            key={`prev-${i}`}
            isOtherMonth
            isDisabled={isDateDisabled(date, minDate, maxDate)}
            onClick={() => handleDateClick(date)}
          >
            {day}
          </DayCell>
        );
      }

      // 현재 달의 날짜들
      for (let day = 1; day <= daysInMonth; day++) {
        const date = new Date(
          currentMonth.getFullYear(),
          currentMonth.getMonth(),
          day
        );
        const isSelected = Boolean(
          (startDate && isSameDay(date, startDate)) ||
            (endDate && isSameDay(date, endDate))
        );
        const isInRange = isDateInRange(date, startDate, endDate);
        const isStartDate = Boolean(startDate && isSameDay(date, startDate));
        const isEndDate = Boolean(endDate && isSameDay(date, endDate));
        const isDisabled = isDateDisabled(date, minDate, maxDate);

        days.push(
          <DayCell
            key={day}
            isSelected={isSelected}
            isInRange={isInRange}
            isStartDate={isStartDate}
            isEndDate={isEndDate}
            isDisabled={isDisabled}
            onClick={() => handleDateClick(date)}
          >
            {day}
          </DayCell>
        );
      }

      // 다음 달의 날짜들 (빈 셀)
      const remainingCells = 42 - days.length; // 6주 * 7일 = 42
      for (let i = 1; i <= remainingCells; i++) {
        const date = new Date(
          currentMonth.getFullYear(),
          currentMonth.getMonth() + 1,
          i
        );

        days.push(
          <DayCell
            key={`next-${i}`}
            isOtherMonth
            isDisabled={isDateDisabled(date, minDate, maxDate)}
            onClick={() => handleDateClick(date)}
          >
            {i}
          </DayCell>
        );
      }

      return days;
    };

    const dayHeaders = ["일", "월", "화", "수", "목", "금", "토"];

    function onClickAway() {
      setIsOpen(false);
      onToggle?.(false);
    }

    return (
      <ClickAwayListener onClickAway={onClickAway}>
        <TextFieldContainer
          ref={ref}
          {...props}
          sx={{ cursor: "pointer", userSelect: "none", ...sx }}
        >
          {label && <LabelText>{label}</LabelText>}

          <InputContainer
            ref={inputRef}
            error={error}
            focused={isOpen}
            onClick={handleToggle}
          >
            <InputContent>
              <CalendarToday
                sx={{ fontSize: "20px", color: "text.secondary" }}
              />
              {startDate ? (
                <InputText>
                  {singleDateOnly
                    ? formatDate(startDate)
                    : formatDateRange(startDate, endDate)}
                </InputText>
              ) : (
                <PlaceholderText>{placeholder}</PlaceholderText>
              )}
            </InputContent>
          </InputContainer>

          <Fade in={isOpen} timeout={160} unmountOnExit>
            <Box sx={getCalendarContainerStyles(alignment, inputRect)}>
              <NavigationContainer>
                <NavButton onClick={handlePrevMonth} size="small">
                  <ChevronLeft />
                </NavButton>
                <MonthYearText>{formatMonthYear(currentMonth)}</MonthYearText>
                <NavButton onClick={handleNextMonth} size="small">
                  <ChevronRight />
                </NavButton>
              </NavigationContainer>

              <DayHeaderContainer>
                {dayHeaders.map((day) => (
                  <DayHeaderCell key={day}>{day}</DayHeaderCell>
                ))}
              </DayHeaderContainer>

              <CalendarGrid>{renderCalendar()}</CalendarGrid>
            </Box>
          </Fade>

          {helperText && <HelperText>{helperText}</HelperText>}
        </TextFieldContainer>
      </ClickAwayListener>
    );
  }
);

DatePicker.displayName = "DatePicker";

export default DatePicker;
